import type { DataSourceParameterType } from '@/types'
import { isInjectedUserIdParameter } from '@/lib/injectedParameters'

export type DataSourceInputParam = {
  name: string
  caption: string
  isRequired: boolean
  parameterType?: DataSourceParameterType
  optionsSql?: string | null
  dependsOn?: string[]
  visible?: boolean
}

/** Example optional single-value filter (empty runtime value = no filter). */
export const OPTIONAL_SCALAR_FILTER_SQL =
  "AND ( '{{param}}' = '' OR column::text = '{{param}}' )"

/** Example optional multi-select filter (comma-separated IDs from UI). */
export const OPTIONAL_MULTI_SELECT_FILTER_SQL = `AND (
    '{{param}}' = ''
    OR column::text = ANY(string_to_array('{{param}}', ','))
)`

export const OPTIONAL_PARAMETER_SQL_HINT_LINES = [
  'Optional filters: wrap placeholders so empty means no filter.',
  `Scalar: ${OPTIONAL_SCALAR_FILTER_SQL.replace(/{{param}}/g, '{{name}}').replace('column', 'your_column')}`,
  "Do not use your_column = '{{name}}' alone for optional parameters.",
] as const

export function hasOptionalParameters(params: DataSourceInputParam[]): boolean {
  return params.some(p => !p.isRequired)
}

export function isDropdownParameterType(t: DataSourceParameterType | undefined): boolean {
  return t === 'Dropdown' || t === 'MultiSelect'
}

export function inferDependsOnFromOptionsSql(optionsSql: string, selfName: string): string[] {
  return extractSqlPlaceholders(optionsSql).filter(n => n !== selfName)
}

export function extractSqlPlaceholders(sql: string): string[] {
  const re = /\{\{([^}]+)\}\}/g
  const seen = new Set<string>()
  const order: string[] = []
  let m: RegExpExecArray | null
  while ((m = re.exec(sql)) !== null) {
    const name = m[1].trim()
    if (!name) continue
    if (!seen.has(name)) {
      seen.add(name)
      order.push(name)
    }
  }
  return order
}

/**
 * Recompute parameter rows from SQL; keep flags for names that still exist.
 * New names default to optional Dropdown; injected userId is always Text (JWT-supplied).
 */
export function mergeInputParameterFlags(sql: string, previous: DataSourceInputParam[]): DataSourceInputParam[] {
  const names = extractSqlPlaceholders(sql)
  const prevMap = new Map(previous.map(p => [p.name, p]))
  return names.map(name => {
    const prev = prevMap.get(name)
    const prevCaption = (prev?.caption ?? '').trim()
    if (isInjectedUserIdParameter(name)) {
      return {
        name,
        caption: prevCaption || name,
        isRequired: prev?.isRequired ?? false,
        parameterType: 'Text' as const,
        optionsSql: null,
        dependsOn: [],
      }
    }
    return {
      name,
      caption: prevCaption || name,
      isRequired: prev?.isRequired ?? false,
      parameterType: prev?.parameterType ?? 'Dropdown',
      optionsSql: prev?.optionsSql ?? null,
      dependsOn: prev?.dependsOn ?? [],
    }
  })
}
